import { useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfile, useUpdateProfile } from './useProfile';
import { languageSchema, type LanguageCode } from '../i18n/schema';
import { safeLocalStorageSet } from '@/lib/safe-local-storage';

/** True when two i18next language tags refer to the same chosen locale (e.g. en vs en-US). */
export function sameLocale(a: string, b: string): boolean {
  if (a === b) return true;
  const baseA = a.split('-')[0] ?? '';
  const baseB = b.split('-')[0] ?? '';
  return baseA.length > 0 && baseA === baseB;
}

/**
 * Hook to sync i18next language with user profile and handle manual changes.
 */
export function useLanguage() {
  const { i18n } = useTranslation();
  const { data: profile } = useProfile();
  const updateProfile = useUpdateProfile();

  // Tracks user-initiated language overrides so the profile-sync effect
  // does not revert a manual switch (e.g. when the mutation's optimistic
  // update rolls back on error and profile.language flips back).
  const manualOverrideRef = useRef<string | null>(null);

  // Sync i18n with profile language when profile loads or changes
  // Fallback chain: User preference > Org default > Browser > 'en' (ADR-013)
  useEffect(() => {
    // While a user-initiated switch is in-flight, don't revert.
    if (manualOverrideRef.current && sameLocale(manualOverrideRef.current, i18n.language)) {
      // Clear once the profile catches up (server confirmed the change).
      if (profile?.language && sameLocale(manualOverrideRef.current, profile.language)) {
        manualOverrideRef.current = null;
      }
      return;
    }

    let languageToUse: string | null = null;

    // 1. User preference (highest priority)
    if (profile?.language) {
      const result = languageSchema.safeParse(profile.language);
      if (result.success) {
        languageToUse = result.data;
      }
    }

    // 2. Organization default (fallback if user language not set)
    if (!languageToUse && profile?.organization?.default_language) {
      const result = languageSchema.safeParse(profile.organization.default_language);
      if (result.success) {
        languageToUse = result.data;
      }
    }

    // 3. Browser/i18n detector will handle the rest (already configured in detection.ts)

    // Apply the language if we found one and it's different from current
    if (languageToUse && !sameLocale(languageToUse, i18n.language)) {
      void i18n.changeLanguage(languageToUse);
      safeLocalStorageSet('codemerlin-language', languageToUse);
    }
  }, [profile?.language, profile?.organization?.default_language, i18n]);

  /**
   * Change language both locally and on the backend.
   */
  const changeLanguage = useCallback(
    async (newLang: string) => {
      const result = languageSchema.safeParse(newLang);
      if (!result.success) {
        console.error(`Invalid language code: ${newLang}`);
        return;
      }

      const validatedLang = result.data;

      // Prevent the profile-sync effect from reverting this switch.
      manualOverrideRef.current = validatedLang;

      // 1. Update local i18n state immediately for snappy UI
      await i18n.changeLanguage(validatedLang);
      safeLocalStorageSet('codemerlin-language', validatedLang);

      // 2. Persist to backend if logged in
      if (profile) {
        try {
          await updateProfile.mutateAsync({
            payload: { language: validatedLang },
            ...(profile.updated_at && { expectedUpdatedAt: profile.updated_at }),
          });
          // Server confirmed — clear the override so the effect is back in control.
          manualOverrideRef.current = null;
        } catch (error) {
          console.error('Failed to persist language preference:', error);
          // Mutation's onError rolled back the optimistic update, which may
          // have triggered the sync effect.  Re-apply the desired language so
          // the user's choice sticks for this session.
          if (!sameLocale(validatedLang, i18n.language)) {
            void i18n.changeLanguage(validatedLang);
          }
        }
      }
    },
    [i18n, profile, updateProfile]
  );

  return {
    language: i18n.language as LanguageCode,
    changeLanguage,
    isUpdating: updateProfile.isPending,
    supportedLanguages: (i18n.options && Array.isArray(i18n.options.supportedLngs)
      ? i18n.options.supportedLngs.filter((l: string) => l !== 'cimode' && l !== 'en-XA')
      : []) as LanguageCode[],
  };
}
